import React, { useState } from 'react';
import {
    LineChart,
    Target,
    Settings,
    LogOut,
    Menu,
    X,
    Droplet,
    Map as MapIcon,
    FileBarChart
} from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Toaster, toast } from 'sonner';
import { useTheme } from '../hooks/useTheme';
import { useNavigationHistory } from '../hooks/useNavigationHistory';
import ExitConfirmDialog from '../components/ExitConfirmDialog';
import { productionService } from '../utils/services/productionService';
import { ProductionAggregate } from '../types/production';
import ProductionGraphs from '../components/ProductionGraphs';
import WellsList from '../components/WellsList';
import UserSettings from '../components/UserSettings';
import { useWells } from '../hooks/useWells';

interface InvestorPortalProps {
    user: any;
    onLogout: () => void;
}

export default function InvestorPortal({ user, onLogout }: InvestorPortalProps) {
    const [activeTab, setActiveTab] = useState('dashboard');
    const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
    const { isDarkMode } = useTheme(user.id);

    // Fetch wells 
    const { wells } = useWells(user.id, user);

    // Filter wells
    const investorWells = user.assignedWells
        ? wells.filter(w => user.assignedWells.includes(w.id))
        : wells;

    // Selected well state for WellsList
    const [selectedWell, setSelectedWell] = useState<any>(null);
    const [productionStats, setProductionStats] = useState<ProductionAggregate | null>(null);

    // 📱 Navigation History - Enable proper back button behavior for PWA
    const { showExitConfirmDialog, handleConfirmExit, handleCancelExit } = useNavigationHistory({
        activeTab,
        setActiveTab,
        selectedWellId: selectedWell?.id || null,
        setSelectedWellId: (id) => {
            if (id) {
                const well = investorWells.find(w => w.id === id);
                setSelectedWell(well || null);
            } else {
                setSelectedWell(null);
            }
        },
        options: {
            defaultTab: 'dashboard',
            preventExit: true,
            showExitDialog: true
        }
    });

    // Fetch Production Data
    React.useEffect(() => {
        const fetchProduction = async () => {
            if (investorWells.length === 0) return;

            const endDate = new Date();
            const startDate = new Date();
            startDate.setDate(startDate.getDate() - 30); // Last 30 days

            const wellIds = investorWells.map(w => w.id);
            const stats = await productionService.getAggregatedProduction(wellIds, startDate, endDate);
            setProductionStats(stats);
        };

        fetchProduction();
    }, [investorWells]);

    const menuItems = [
        { id: 'dashboard', label: 'Overview', icon: LineChart },
        { id: 'wells', label: 'My Locations', icon: MapIcon },
        { id: 'reports', label: 'Reports', icon: FileBarChart },
        { id: 'settings', label: 'Settings', icon: Settings },
    ];

    const renderContent = () => {
        switch (activeTab) {
            case 'dashboard':
                return (
                    <div className="space-y-6">
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
                            <Card className="bg-gradient-to-br from-blue-500 to-blue-600 text-white border-0">
                                <CardContent className="p-6">
                                    <div className="flex items-center justify-between mb-4">
                                        <p className="text-blue-100">Total Production (30d)</p>
                                        <Droplet className="w-5 h-5 text-blue-200" />
                                    </div>
                                    <h3 className="text-3xl font-bold">
                                        {productionStats ? Math.round(productionStats.totalOil).toLocaleString() : '-'} <span className="text-lg font-normal text-blue-200">BBL</span>
                                    </h3>
                                    <p className="text-sm text-blue-100 mt-2">
                                        {productionStats ? `${Math.round(productionStats.averageOil)} BBL/day avg` : 'Loading...'}
                                    </p>
                                </CardContent>
                            </Card>
                            <Card className="bg-gradient-to-br from-emerald-500 to-emerald-600 text-white border-0">
                                <CardContent className="p-6">
                                    <div className="flex items-center justify-between mb-4">
                                        <p className="text-emerald-100">Active Wells</p>
                                        <Target className="w-5 h-5 text-emerald-200" />
                                    </div>
                                    <h3 className="text-3xl font-bold">
                                        {investorWells.filter(w => w.status === 'active').length} / {investorWells.length}
                                    </h3>
                                    <p className="text-sm text-emerald-100 mt-2">Operational Efficiency</p>
                                </CardContent>
                            </Card>
                            <Card className="bg-white dark:bg-slate-800 border-slate-200 dark:border-slate-700">
                                <CardContent className="p-6">
                                    <div className="flex items-center justify-between mb-4">
                                        <p className="text-slate-500 dark:text-slate-400">Est. Revenue (30d)</p>
                                        <DollarSignIcon className="w-5 h-5 text-slate-400" />
                                    </div>
                                    <h3 className="text-3xl font-bold text-slate-900 dark:text-white">
                                        {productionStats ? `$${(Math.round(productionStats.totalOil * 75)).toLocaleString()}` : '-'}
                                    </h3>
                                    <p className="text-sm text-slate-500 mt-2">@ $75/bbl</p>
                                </CardContent>
                            </Card>
                        </div>

                        <div className="bg-white dark:bg-slate-900 rounded-xl shadow-sm border border-slate-200 dark:border-slate-800 p-6">
                            <h3 className="text-lg font-bold text-slate-900 dark:text-white mb-6">Production Metrics</h3>
                            <ProductionGraphs wells={investorWells} />
                        </div>
                    </div>
                );
            case 'wells':
                return (
                    <div className="space-y-6">
                        <h2 className="text-2xl font-bold text-slate-900 dark:text-white">Assigned Locations</h2>
                        <WellsList
                            wells={investorWells}
                            selectedWell={selectedWell}
                            onSelectWell={setSelectedWell}
                            overdueWells={[]} // Investors probably don't track overdue maintenance
                        // No onEditWell or onDeleteWell passed -> implicit view only for buttons
                        />
                    </div>
                );
            case 'reports':
                return (
                    <div className="flex flex-col items-center justify-center h-96 text-center space-y-4">
                        <div className="w-16 h-16 bg-blue-100 dark:bg-blue-900/30 text-blue-600 rounded-full flex items-center justify-center">
                            <FileBarChart className="w-8 h-8" />
                        </div>
                        <div>
                            <h3 className="text-xl font-bold text-slate-900 dark:text-white">Quarterly Reports</h3>
                            <p className="text-slate-500 max-w-md mx-auto mt-2">
                                Your detailed financial and production reports will appear here once generated by the operator.
                            </p>
                        </div>
                        <Button variant="outline">Request Archive</Button>
                    </div>
                );
            case 'settings':
                return (
                    <UserSettings
                        user={user}
                        onClose={() => setActiveTab('dashboard')}
                        onSave={() => toast.success('Settings updated')}
                        isDarkMode={isDarkMode}
                    />
                );
            default:
                return null;
        }
    };

    return (
        <div className={`min-h-screen ${isDarkMode ? 'dark bg-slate-950' : 'bg-slate-50'}`}>
            <Toaster position="top-right" />

            {/* Sidebar Navigation */}
            <aside className={`
        fixed top-0 left-0 z-40 h-screen transition-transform bg-white dark:bg-slate-900 border-r border-slate-200 dark:border-slate-800
        ${isMobileMenuOpen ? 'translate-x-0' : '-translate-x-full'} md:translate-x-0 w-64
      `}>
                <div className="h-full flex flex-col">
                    {/* Logo Area */}
                    <div className="h-16 flex items-center px-6 border-b border-slate-200 dark:border-slate-800">
                        <div className="flex items-center gap-2 text-indigo-600 dark:text-indigo-400 font-bold text-xl">
                            <LineChart className="w-6 h-6" />
                            <span>InvestorPortal</span>
                        </div>
                    </div>

                    {/* User Profile */}
                    <div className="p-4 border-b border-slate-200 dark:border-slate-800 bg-slate-50 dark:bg-slate-800/50">
                        <div className="flex items-center gap-3">
                            <div className="w-10 h-10 rounded-full bg-indigo-100 dark:bg-indigo-900 flex items-center justify-center text-indigo-700 dark:text-indigo-300 font-bold">
                                {user.name?.charAt(0) || 'I'}
                            </div>
                            <div className="overflow-hidden">
                                <p className="text-sm font-semibold text-slate-900 dark:text-slate-100 truncate">
                                    {user.name}
                                </p>
                                <p className="text-xs text-slate-500 dark:text-slate-400 truncate">
                                    {user.company || 'Private Investor'}
                                </p>
                            </div>
                        </div>
                    </div>

                    {/* Navigation Items */}
                    <nav className="flex-1 p-4 space-y-1 overflow-y-auto">
                        {menuItems.map((item) => (
                            <button
                                key={item.id}
                                onClick={() => {
                                    setActiveTab(item.id);
                                    setIsMobileMenuOpen(false);
                                }}
                                className={`
                  w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors
                  ${activeTab === item.id
                                        ? 'bg-indigo-50 text-indigo-700 dark:bg-indigo-900/30 dark:text-indigo-400'
                                        : 'text-slate-700 hover:bg-slate-100 dark:text-slate-400 dark:hover:bg-slate-800'}
                `}
                            >
                                <item.icon className="w-5 h-5" />
                                {item.label}
                            </button>
                        ))}
                    </nav>

                    {/* Footer Actions */}
                    <div className="p-4 border-t border-slate-200 dark:border-slate-800">
                        <button
                            onClick={onLogout}
                            className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium text-slate-600 hover:bg-slate-100 dark:text-slate-400 dark:hover:bg-slate-800 transition-colors"
                        >
                            <LogOut className="w-5 h-5" />
                            Sign Out
                        </button>
                    </div>
                </div>
            </aside>

            {/* Main Content Area */}
            <main className="md:ml-64 min-h-screen flex flex-col transition-all duration-300">
                {/* Mobile Header */}
                <header className="md:hidden h-16 bg-white dark:bg-slate-900 border-b border-slate-200 dark:border-slate-800 flex items-center justify-between px-4 sticky top-0 z-30">
                    <div className="flex items-center gap-2 text-indigo-600 font-bold text-lg">
                        <LineChart className="w-5 h-5" />
                        <span>InvestorPortal</span>
                    </div>
                    <button
                        onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
                        className="p-2 text-slate-600 hover:bg-slate-100 rounded-lg"
                    >
                        {isMobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
                    </button>
                </header>

                {/* Content Container */}
                <div className="flex-1 p-4 md:p-8">
                    <div className="max-w-7xl mx-auto">
                        {renderContent()}
                    </div>
                </div>
            </main>

            {/* Mobile Overlay */}
            {isMobileMenuOpen && (
                <div
                    className="fixed inset-0 bg-black/50 z-30 md:hidden"
                    onClick={() => setIsMobileMenuOpen(false)}
                />
            )}

            {/* Exit Confirmation Dialog - shown when user presses back twice */}
            <ExitConfirmDialog
                open={showExitConfirmDialog}
                onConfirm={handleConfirmExit}
                onCancel={handleCancelExit}
            />
        </div>
    );
}

// Helper Icon
function DollarSignIcon(props: any) {
    return (
        <svg
            {...props}
            xmlns="http://www.w3.org/2000/svg"
            width="24"
            height="24"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
        >
            <line x1="12" x2="12" y1="2" y2="22" />
            <path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6" />
        </svg>
    )
}
